import PropTypes from 'prop-types';
import React from 'react';
import colors from '@loggi/ui/components/utils/colors';
import Br from './br';
import Space from './space';
import weightStyles from './weight.scss';
import alignStyles from './align.scss';

const { getTextElement } = __NATIVE__ ? require('./native') : require('./dom');

export const TEXT_SIZE_REGULAR = 'regular';
export const TEXT_SIZE_SMALL = 'small';
export const TEXT_SIZE_LARGE = 'large';
export const TEXT_SIZES = [
  '100',
  '200',
  '300',
  '400',
  '500',
  '600',
  '700',
  '800',
  '900',
  '1000',
  '1100',
  '1200'
];
export const TEXT_SIZE_OPTIONS = [
  TEXT_SIZE_REGULAR,
  TEXT_SIZE_SMALL,
  TEXT_SIZE_LARGE,
  ...TEXT_SIZES
];

export const TEXT_TYPE_REGULAR = 'text';
export const TEXT_TYPE_DISPLAY = 'display';
export const TEXT_TYPE_TITLE = 'title';
export const TEXT_TYPE_OUTDOOR = 'outdoor';
export const TEXT_TYPE_OPTIONS = [
  TEXT_TYPE_REGULAR,
  TEXT_TYPE_DISPLAY,
  TEXT_TYPE_TITLE,
  TEXT_TYPE_OUTDOOR
];

export const TEXT_WEIGHT_REGULAR = 'regular';
export const TEXT_WEIGHT_BOLD = 'bold';
export const TEXT_WEIGHT_BOLDER = 'bolder';
export const TEXT_WEIGHT_OPTIONS = [
  TEXT_WEIGHT_REGULAR,
  TEXT_WEIGHT_BOLD,
  TEXT_WEIGHT_BOLDER
];

export const TEXT_ALIGN_CENTER = 'center';
export const TEXT_ALIGN_LEFT = 'left';
export const TEXT_ALIGN_RIGHT = 'right';
export const TEXT_ALIGN_OPTIONS = [
  TEXT_ALIGN_CENTER,
  TEXT_ALIGN_LEFT,
  TEXT_ALIGN_RIGHT
];

export const TEXT_FORMAT_DATE = 'date';
export const TEXT_FORMAT_CPF = 'cpf';
export const TEXT_FORMAT_CNPJ = 'cnpj';
export const TEXT_FORMAT_PHONE = 'phone';
export const TEXT_FORMAT_CEP = 'cep';
export const TEXT_FORMAT_OPTIONS = [
  TEXT_FORMAT_DATE,
  TEXT_FORMAT_CPF,
  TEXT_FORMAT_CNPJ,
  TEXT_FORMAT_PHONE,
  TEXT_FORMAT_CEP
];

export const TEXT_CLASS_MAPPER = {
  [TEXT_TYPE_REGULAR]: {
    [TEXT_SIZE_REGULAR]: 'text-base',
    [TEXT_SIZE_LARGE]: 'text-lg',
    [TEXT_SIZE_SMALL]: 'text-sm'
  },

  [TEXT_TYPE_TITLE]: {
    [TEXT_SIZE_REGULAR]: 'title-regular',
    [TEXT_SIZE_LARGE]: 'title-large',
    [TEXT_SIZE_SMALL]: 'title-small'
  },

  [TEXT_TYPE_DISPLAY]: {
    [TEXT_SIZE_REGULAR]: 'display-regular',
    [TEXT_SIZE_LARGE]: 'display-large',
    [TEXT_SIZE_SMALL]: 'display-small'
  },

  [TEXT_TYPE_OUTDOOR]: {
    [TEXT_SIZE_REGULAR]: 'outdoor-regular',
    [TEXT_SIZE_LARGE]: 'outdoor-large',
    [TEXT_SIZE_SMALL]: 'outdoor-small'
  }
};

const DOM_TAG_DIV = 'div';
const DOM_TAG_SPAN = 'span';
const DOM_TAG_H1 = 'h1';

const TEXT_DOM_ELEMENT_MAPPER = {
  [TEXT_TYPE_REGULAR]: DOM_TAG_SPAN,
  [TEXT_TYPE_TITLE]: DOM_TAG_H1,
  [TEXT_TYPE_DISPLAY]: DOM_TAG_H1,
  [TEXT_TYPE_OUTDOOR]: DOM_TAG_H1
};

const PROP_TYPE = 'type';
const PROP_SIZE = 'size';
const PROP_WEIGHT = 'weight';
const PROP_ALIGN = 'align';
const PROP_FORMAT = 'format';
const PROPS_SHORTCUTS = {
  display: PROP_TYPE,
  title: PROP_TYPE,
  bold: PROP_WEIGHT,
  bolder: PROP_WEIGHT,
  large: PROP_SIZE,
  small: PROP_SIZE,
  center: PROP_ALIGN,
  left: PROP_ALIGN,
  right: PROP_ALIGN,
  date: PROP_FORMAT,
  cpf: PROP_FORMAT,
  cnpj: PROP_FORMAT,
  phone: PROP_FORMAT,
  cep: PROP_FORMAT
};

const sizeRetroCompatibility = (size, type) => {
  if (type === TEXT_TYPE_REGULAR && TEXT_SIZES.includes(size)) {
    return `font-size-${size}`;
  } else {
    const fallbackSize = TEXT_SIZES.includes(size) ? TEXT_SIZE_REGULAR : size;
    console.warn(
      '[ELKE] The `type` prop from Text component will be discontinued in the next version, change this implementation according with the new rules as soon as possible. Run the elke storybook localy for more informations or get support on #elke slack channel'
    );
    return TEXT_CLASS_MAPPER[type][fallbackSize];
  }
};

class Text extends React.Component {
  static displayName = 'Text';

  static defaultProps = {
    size: '300',
    type: TEXT_TYPE_REGULAR,
    color: __NATIVE__ ? colors.type.DARK : null,
    align: __NATIVE__ ? TEXT_ALIGN_LEFT : null,
    format: null,
    elementMapper: __NATIVE__ ? null : TEXT_DOM_ELEMENT_MAPPER,
    ellipsis: null,
    onOverflow: () => {}
  };

  static propTypes = {
    // Properties
    elementMapper: PropTypes.object,
    weight: PropTypes.oneOf(TEXT_WEIGHT_OPTIONS),
    size: PropTypes.oneOf(TEXT_SIZE_OPTIONS),
    type: PropTypes.oneOf(TEXT_TYPE_OPTIONS),
    color: PropTypes.oneOf(colors.typeOptions),
    align: PropTypes.oneOf(TEXT_ALIGN_OPTIONS),
    format: PropTypes.oneOf(TEXT_FORMAT_OPTIONS),
    children: PropTypes.any.isRequired,
    ellipsis: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.object // react native purpose
    ]),
    onOverflow: PropTypes.func,

    // Shortcuts
    ...Object.keys(PROPS_SHORTCUTS).reduce((obj, prop) => {
      obj[prop] = PropTypes.bool;
      return obj;
    }, {})
  };

  constructor(props) {
    super(props);
    this.textRef = React.createRef();
  }

  componentDidMount() {
    const { current } = this.textRef;
    const { onOverflow } = this.props;

    if (current) {
      onOverflow(current.scrollWidth > current.clientWidth);
    }
  }

  render() {
    const {
      type,
      size,
      weight,
      color,
      align,
      format,
      children,
      ellipsis,
      elementMapper,
      ...shortcuts
    } = this.props;
    const attributes = { type, size, weight, color, align, format };

    // Override props with shortcuts if they exist
    Object.keys(shortcuts)
      .filter(p => PROPS_SHORTCUTS[p] && shortcuts[p] === true)
      .forEach(p => {
        const prop = PROPS_SHORTCUTS[p];
        attributes[prop] = p;
      });

    const textSizeClasses = sizeRetroCompatibility(size, type);

    // Define styles
    const textStyles = [
      textSizeClasses,
      weightStyles[attributes.weight],
      alignStyles[attributes.align]
    ];

    let mapper = elementMapper;

    // change regular for a block element
    if (ellipsis) {
      mapper = Object.assign({}, mapper, {
        [TEXT_TYPE_REGULAR]: DOM_TAG_DIV
      });
    }

    // Get element according to it's type & size
    const props = { children };
    const ref = this.textRef;

    return getTextElement(mapper, attributes, textStyles, props, ellipsis, ref);
  }
}

export { Br };
export { Space };
export default Text;
